import { faClone } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, Typography } from "@mui/material"
import { IAdvertiserFormData } from "advertiser/model"
import { advertiserRPO } from "advertiser/repository"
import { createAdvertiser, updateAdvertiser } from "advertiser/tools/common"
import { validateAdvertiserFormData } from "advertiser/tools/form"
import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { deepEquals } from "app/tools/common"
import { FieldText } from "form/components/fields/FieldText"
import { Form, Formik } from "formik"
import { messageSVC } from "message/service"
import { ADVERTISER_UPDATE_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"

interface IAdvertiserFormProps {
  advertiserId: number | undefined
  onInitDataError?: (errorCode: number) => any
}

export const AdvertiserForm = React.memo(({ advertiserId, onInitDataError }: IAdvertiserFormProps): JSX.Element => {
  const [initialData, setInitialData] = React.useState<IAdvertiserFormData | null>(null)
  const [data, setData] = React.useState<IAdvertiserFormData | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isValid, setIsValid] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [hasLoadingError, setHasLoadingError] = React.useState(false)
  const hasChanged = React.useMemo(() => !deepEquals(initialData, data), [initialData, data])
  const isCreation = typeof advertiserId !== "number"
  React.useEffect(() => {
    if (isCreation) {
      const newData = {
        id: NOT_SAVED_ID,
        name: "",
        media_fee_percent: 0
      }
      setData(newData)
      setInitialData({ ...newData })
    } else {
      setIsLoading(true)
      advertiserRPO
        .getAdvertiser(Number(advertiserId))
        .then(fetchedAdvertiser => {
          const newData = {
            id: fetchedAdvertiser.id,
            name: fetchedAdvertiser.name || "",
            media_fee_percent: fetchedAdvertiser.media_fee_percent
          }
          setData(newData)
          setInitialData({ ...newData })
          setIsLoading(false)
        })
        .catch(error => {
          console.error(error)
          setIsLoading(false)
          setHasLoadingError(true)
          if (onInitDataError) {
            onInitDataError(typeof error === "string" && error.includes("not found") ? 404 : 500)
          }
        })
    }
  }, [])
  if (hasLoadingError) {
    return (
      <Typography fontSize=".9rem" color="error" sx={{ mt: "2rem" }}>
        {translate("label.list.error")}
      </Typography>
    )
  }
  if (!data || isLoading) {
    return <LinearLoader text={translate("advertiser.loading")} />
  }
  return (
    <Formik
      initialValues={data}
      validate={(values: IAdvertiserFormData) => {
        setData(values)
        const errors = validateAdvertiserFormData(values)
        if (JSON.stringify(errors) === "{}") {
          if (!isValid) {
            setIsValid(true)
          }
        } else if (isValid) {
          setIsValid(false)
        }
        return errors
      }}
      onSubmit={async (_values, actions) => {
        if (isSaving) {
          return
        }
        setIsSaving(true)
        try {
          if (isCreation) {
            const newAdvertiser = await createAdvertiser(data)
            messageSVC.success(translate("advertiser.add.success", newAdvertiser.name))
            navigate({ path: ADVERTISER_UPDATE_PATH, pathParams: newAdvertiser.id })
          } else {
            await updateAdvertiser(advertiserId, data)
            messageSVC.success(translate("advertiser.update.success", data.name))
            setInitialData(data)
            setIsSaving(false)
          }
        } catch (error) {
          console.error(error)
          messageSVC.error(translate("advertiser.error.unknown"))
          setIsSaving(false)
          actions.setSubmitting(false)
        }
      }}>
      {({ errors, isSubmitting }) => {
        const canSubmit = !isSubmitting && isValid
        return (
          <Form style={{ maxWidth: "100%" }}>
            <FlexBox
              wrap
              gap
              vertical
              fullWidth
              sx={{
                "maxWidth": "100%",
                "& > *": {
                  width: "25rem",
                  maxWidth: "100%"
                }
              }}>
              <FieldText
                fieldName="name"
                label={translate("label.name")}
                value={data.name}
                error={errors.name}
                required
              />
              <FieldText
                fieldName="media_fee_percent"
                label={translate("label.media_fee_percent")}
                value={data.media_fee_percent}
                error={errors.media_fee_percent}
                endAdornmentText="%"
                isNumber
                clearable
              />
            </FlexBox>
            <FlexBox gap fullWidth ai="end" jc="end" sx={{ mt: "2rem" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  // TODO cancel changes
                }}
                disabled={isSaving || !hasChanged}>
                {translate("button.cancel")}
              </Button>
              <Button
                variant="contained"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress sx={{ color: theme => theme.palette.primary.main }} size="1.5rem" />
                  ) : (
                    <FontAwesomeIcon icon={faClone} />
                  )
                }
                type="submit"
                disabled={!hasChanged || !canSubmit || isSaving}>
                {translate(isCreation ? "button.create" : "button.update")}
              </Button>
            </FlexBox>
          </Form>
        )
      }}
    </Formik>
  )
})
